import { useEffect, useState } from "react";
import { FileKey2, FileUp, ShieldCheck } from "lucide-react";
import { bugReportApi, AdminApiError, type BugReport } from "../api";
import { CopyButton, humanSize, useToast } from "../ui";

/* Sign a build — everything happens in this tab. The JAR and the ed25519
 * private key are read with FileReader and signed via WebCrypto; neither
 * ever leaves the browser. The output signature (base64) is what the worker
 * stores alongside the JAR, and what the buyer's plugin verifies against its
 * embedded public key before staging.
 *
 * Same math as tools/sign-jar.mjs — this is just the no-terminal path. */

type Signed = {
  jarName: string;
  jarSize: number;
  signature: string;
  sha256: string;
};

export function SignPage() {
  const toast = useToast();
  const [jar, setJar] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState<Signed | null>(null);
  const [cryptoOk, setCryptoOk] = useState(true);

  // Ed25519 in WebCrypto needs a current browser; probe once so we can show
  // the CLI fallback instead of a cryptic DOMException.
  useEffect(() => {
    (async () => {
      try {
        await crypto.subtle.generateKey("Ed25519", false, ["sign", "verify"]);
      } catch {
        setCryptoOk(false);
      }
    })();
  }, []);

  const sign = async () => {
    if (!jar || !keyFile) return;
    setBusy(true);
    setSigned(null);
    try {
      const [jarBytes, keyText] = await Promise.all([jar.arrayBuffer(), keyFile.text()]);
      const der = Uint8Array.from(atob(keyText.trim()), (c) => c.charCodeAt(0));
      const key = await crypto.subtle.importKey("pkcs8", der, "Ed25519", false, ["sign"]);
      const sig = new Uint8Array(await crypto.subtle.sign("Ed25519", key, jarBytes));
      const sha = new Uint8Array(await crypto.subtle.digest("SHA-256", jarBytes));
      setSigned({
        jarName: jar.name,
        jarSize: jar.size,
        signature: btoa(String.fromCharCode(...sig)),
        sha256: [...sha].map((b) => b.toString(16).padStart(2, "0")).join(""),
      });
    } catch (e) {
      toast(
        "err",
        e instanceof Error && e.name === "DataError"
          ? "That doesn't look like the pkcs8 base64 private key"
          : "Signing failed — check the key file",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rise-in">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Sign a build</h1>
        <p className="mt-1 text-sm text-fg-mute">
          Signs a hotfix JAR with your build-signing key, right here in the browser — the key is
          read locally and never uploaded anywhere.
        </p>
      </header>

      {!cryptoOk && (
        <div className="card mt-8 p-5 text-sm text-fg-mute">
          This browser can&rsquo;t do Ed25519 in WebCrypto. Use the CLI instead:
          <pre className="mt-2.5 overflow-x-auto rounded-(--radius-field) bg-ink-950 p-3 font-mono text-xs text-fg-mute">
            node cloudflare/tools/sign-jar.mjs path/to/Heartline.jar
          </pre>
        </div>
      )}

      <div className="card mt-8 space-y-5 p-6">
        <FilePick
          icon={<FileUp className="h-4 w-4" />}
          label="Hotfix JAR"
          hint="The exact bytes the buyer will run."
          accept=".jar"
          file={jar}
          onPick={(f) => {
            setJar(f);
            setSigned(null);
          }}
        />
        <FilePick
          icon={<FileKey2 className="h-4 w-4" />}
          label="Private key"
          hint="build-signing-key.pk8.b64 — stays on this machine."
          accept=".b64,.pk8,.txt"
          file={keyFile}
          onPick={(f) => {
            setKeyFile(f);
            setSigned(null);
          }}
        />
        <button className="btn-primary w-full" disabled={busy || !jar || !keyFile || !cryptoOk} onClick={sign}>
          <ShieldCheck className="h-4 w-4" /> {busy ? "Signing…" : "Sign"}
        </button>
      </div>

      {signed && <SignedResult signed={signed} jar={jar!} />}
    </div>
  );
}

function FilePick({
  icon,
  label,
  hint,
  accept,
  file,
  onPick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  accept: string;
  file: File | null;
  onPick: (f: File | null) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3.5 rounded-(--radius-field) bg-ink-850 p-3.5 transition hover:bg-ink-800">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-700 text-rose-400">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">
          {file ? file.name : label}
          {file && <span className="ml-2 text-xs font-normal text-fg-faint">{humanSize(file.size)}</span>}
        </span>
        <span className="block text-xs text-fg-faint">{hint}</span>
      </span>
      <input type="file" accept={accept} className="hidden" onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
    </label>
  );
}

function SignedResult({ signed, jar }: { signed: Signed; jar: File }) {
  const toast = useToast();
  const [tickets, setTickets] = useState<BugReport[]>([]);
  const [target, setTarget] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(false);

  useEffect(() => {
    bugReportApi
      .list()
      .then((r) => setTickets((r.reports ?? []).filter((t) => t.status === "open" || t.status === "ack")))
      .catch(() => {});
  }, []);

  return (
    <div className="card rise-in mt-5 space-y-4 p-6">
      <div className="flex items-center gap-2 text-sm font-semibold text-ok">
        <ShieldCheck className="h-4 w-4" /> Signed
      </div>

      <div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-fg-mute">Signature (base64)</span>
          <CopyButton text={signed.signature} />
        </div>
        <div className="mt-1.5 rounded-(--radius-field) bg-ink-950 p-3 font-mono text-[11px] break-all text-fg-mute">
          {signed.signature}
        </div>
      </div>

      <div className="text-xs text-fg-faint">
        <span className="font-mono">{signed.jarName}</span> · {humanSize(signed.jarSize)} · sha256{" "}
        <span className="font-mono">{signed.sha256.slice(0, 16)}…</span>
      </div>

      {/* One-click attach: skips the copy-paste round-trip through Tickets. */}
      <div className="rounded-(--radius-field) bg-ink-900 p-3.5">
        <div className="text-xs font-medium text-fg-mute">Attach to a ticket</div>
        {attached ? (
          <p className="mt-2 text-sm text-ok">Attached — the buyer&rsquo;s Studio now offers the hotfix.</p>
        ) : (
          <div className="mt-2 flex gap-2">
            <select className="field flex-1" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">Pick an open ticket…</option>
              {tickets.map((t) => (
                <option key={t.id} value={t.id}>
                  {(t.entity || "unknown") + " — " + (t.description || "").split("\n")[0].slice(0, 60)}
                </option>
              ))}
            </select>
            <button
              className="btn-primary shrink-0"
              disabled={!target || attaching}
              onClick={async () => {
                setAttaching(true);
                try {
                  await bugReportApi.uploadBuild(target, jar, signed.signature);
                  setAttached(true);
                  toast("ok", "Build attached");
                } catch (e) {
                  toast("err", e instanceof AdminApiError ? e.message : "Attach failed");
                } finally {
                  setAttaching(false);
                }
              }}
            >
              {attaching ? "Attaching…" : "Attach"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
